import React, { useCallback, useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import { Card, Col, Row, Statistic, Table, message, Button } from "antd";
import { Link } from "react-router-dom";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { getMerchantSettings } from "../../utils/redux/reducers/Merchant";
import { ApiManager, Locale } from "../../utils";
import "./StoreMembershipList.scss";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();

function StoreMembershipAttributionStats({ user }) {
  const settings = useSelector((state) => getMerchantSettings(state));
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState({});

  const fetchStats = useCallback(async () => {
    const { token } = user || {};
    if (!token) return;
    setLoading(true);
    try {
      const res = await api.storeMembership.attributionStats.run({ token });
      if (res.result) {
        setItems(res.data?.items || []);
        setTotal(res.data?.total || {});
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (!settings?.wholesale_store_verification) {
    return (
      <div className="store-membership-page">
        {i18n.t("storeMembership.enableHint")}
      </div>
    );
  }

  const columns = [
    {
      title: i18n.t("storeMembership.attribution"),
      dataIndex: "label",
      key: "label",
    },
    {
      title: i18n.t("storeMembership.status_all"),
      dataIndex: "all",
      key: "all",
    },
    {
      title: i18n.t("storeMembership.status_approved"),
      dataIndex: "approved",
      key: "approved",
    },
    {
      title: i18n.t("storeMembership.status_pending"),
      dataIndex: "pending",
      key: "pending",
    },
    {
      title: i18n.t("storeMembership.status_rejected"),
      dataIndex: "rejected",
      key: "rejected",
    },
    {
      title: i18n.t("storeMembership.status_banned"),
      dataIndex: "banned",
      key: "banned",
    },
    {
      title: i18n.t("storeMembership.actions"),
      key: "actions",
      render: (_, row) => (
        <Link
          to={`/main/users/store-memberships?attribution=${encodeURIComponent(
            row.key === "unassigned" ? "unassigned" : row.key
          )}`}
        >
          {i18n.t("storeMembership.viewList")}
        </Link>
      ),
    },
  ];

  const ownerCards = (items || []).filter((item) => item.key !== "unassigned");

  return (
    <div className="store-membership-page">
      <Card
        bordered={false}
        className="store-membership-card"
        title={i18n.t("storeMembership.attributionStatsTitle")}
        extra={
          <Button onClick={fetchStats} loading={loading}>
            {i18n.t("storeMembership.refresh")}
          </Button>
        }
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={8} md={6}>
            <Card size="small">
              <Statistic
                title={i18n.t("storeMembership.status_all")}
                value={total.all || 0}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Card size="small">
              <Statistic
                title={i18n.t("storeMembership.status_approved")}
                value={total.approved || 0}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Card size="small">
              <Statistic
                title={i18n.t("storeMembership.status_pending")}
                value={total.pending || 0}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8} md={6}>
            <Card size="small">
              <Statistic
                title={i18n.t("storeMembership.unassigned")}
                value={
                  (items || []).find((i) => i.key === "unassigned")?.all || 0
                }
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          {ownerCards.map((item) => (
            <Col xs={24} sm={12} md={8} key={item.key}>
              <Card size="small" title={item.label}>
                <Statistic
                  title={i18n.t("storeMembership.status_all")}
                  value={item.all || 0}
                />
                <div style={{ marginTop: 12, color: "#666", fontSize: 13 }}>
                  {i18n.t("storeMembership.status_approved")}: {item.approved || 0}
                  {" · "}
                  {i18n.t("storeMembership.status_pending")}: {item.pending || 0}
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <Table
          rowKey="key"
          loading={loading}
          columns={columns}
          dataSource={items}
          pagination={false}
        />
      </Card>
    </div>
  );
}

export default connect((state) => ({
  user: getUserInfo(state),
}))(StoreMembershipAttributionStats);
